import {
  OrganizationManager as OrganizationManagerEntity,
  type OrganizationManager,
  type VolunteerOrganizationLead,
} from "@entities";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { organizationManagerRepo, volunteerOrganizationLeadRepo } from "@repositories";
import { normalizePhoneDigitsForSearch } from "@shared/helpers/normalize-phone-for-search";
import {
  PARTNER_ORG_LEAD_CONTACT_CHANNEL_OPTIONS,
  type PartnerOrganizationLeadContactChannel,
} from "@shared/helpers/partner-organization-lead-contact";
import { useCallback, useEffect, useState } from "react";
import { ChoiceTile } from "./volunteer-org-form-ui";

type LeadFormValues = {
  name: string;
  handlingManagerId: string;
  phoneNumber: string;
  contactChannel: PartnerOrganizationLeadContactChannel | "";
  notes: string;
};

const emptyValues: LeadFormValues = {
  name: "",
  handlingManagerId: "",
  phoneNumber: "",
  contactChannel: "",
  notes: "",
};

type Props = {
  open: boolean;
  lead: VolunteerOrganizationLead | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

export function VolunteerOrganizationLeadDialog({
  open,
  lead,
  onClose,
  onSaved,
}: Props) {
  const [values, setValues] = useState<LeadFormValues>(emptyValues);
  const [responsibleManager, setResponsibleManager] =
    useState<OrganizationManager | null>(null);
  const [responsibleManagerSearch, setResponsibleManagerSearch] = useState("");
  const [responsibleManagerOptions, setResponsibleManagerOptions] = useState<
    OrganizationManager[]
  >([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setResponsibleManagerSearch("");
    if (lead) {
      setValues({
        name: lead.name ?? "",
        handlingManagerId: lead.handlingManagerId ?? "",
        phoneNumber: lead.phoneNumber ?? "",
        contactChannel: lead.contactChannel ?? "",
        notes: lead.notes ?? "",
      });
      setResponsibleManager(lead.handlingManager ?? null);
      setResponsibleManagerOptions(
        lead.handlingManager ? [lead.handlingManager] : [],
      );
      if (!lead.handlingManager && lead.handlingManagerId) {
        void organizationManagerRepo.findId(lead.handlingManagerId).then((manager) => {
          if (!manager) return;
          setResponsibleManager(manager);
          setResponsibleManagerOptions([manager]);
        });
      }
    } else {
      setValues(emptyValues);
      setResponsibleManager(null);
      setResponsibleManagerOptions([]);
    }
  }, [open, lead]);

  const searchResponsibleManagers = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      setResponsibleManagerOptions([]);
      return;
    }
    const phoneQuery = normalizePhoneDigitsForSearch(q);
    const list = await organizationManagerRepo.find({
      where: OrganizationManagerEntity.hyperFilter({
        $or: [
          { name: { $contains: q } },
          { phoneNumber: { $contains: phoneQuery } },
        ],
      }),
      include: { organization: true },
      orderBy: { name: "asc" },
      limit: 25,
    });
    setResponsibleManagerOptions(list);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      void searchResponsibleManagers(responsibleManagerSearch);
    }, 300);
    return () => clearTimeout(t);
  }, [responsibleManagerSearch, searchResponsibleManagers]);

  const handleSave = async () => {
    const name = values.name.trim();
    if (!name) {
      setError("שם הארגון חובה");
      return;
    }
    if (!values.handlingManagerId) {
      setError("מנהל מלווה חובה");
      return;
    }
    const phoneNumber = values.phoneNumber.trim();
    if (!phoneNumber) {
      setError("מספר טלפון חובה");
      return;
    }
    if (!values.contactChannel) {
      setError("דרך יצירת קשר חובה");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name,
        handlingManagerId: values.handlingManagerId,
        phoneNumber,
        contactChannel: values.contactChannel,
        notes: values.notes.trim() || null,
      };
      if (lead) {
        await volunteerOrganizationLeadRepo.update(lead.id, payload);
      } else {
        await volunteerOrganizationLeadRepo.insert(payload);
      }
      await onSaved();
      onClose();
    } catch {
      setError("שמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700 }}>
          {lead ? "עריכת ליד" : "ליד חדש"}
        </Typography>
        <IconButton aria-label="סגירה" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2.5} sx={{ pt: 1 }}>
          <TextField
            label="שם הארגון"
            value={values.name}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, name: event.target.value }))
            }
            fullWidth
            autoFocus
          />
          <TextField
            label="טלפון"
            value={values.phoneNumber}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, phoneNumber: event.target.value }))
            }
            fullWidth
            inputProps={{ inputMode: "tel" }}
          />
          <Autocomplete
            options={
              responsibleManager &&
              !responsibleManagerOptions.some(
                (manager) => manager.id === responsibleManager.id,
              )
                ? [responsibleManager, ...responsibleManagerOptions]
                : responsibleManagerOptions
            }
            value={responsibleManager}
            onChange={(_, manager) => {
              setResponsibleManager(manager);
              setValues((prev) => ({
                ...prev,
                handlingManagerId: manager?.id ?? "",
              }));
            }}
            inputValue={responsibleManagerSearch}
            onInputChange={(_, value) => setResponsibleManagerSearch(value)}
            getOptionLabel={(manager) =>
              `${manager.name}${manager.organization?.name ? ` · ${manager.organization.name}` : ""}`
            }
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="הקלד לפחות 2 תווים לחיפוש"
            renderInput={(params) => (
              <TextField {...params} label="מנהל מלווה" />
            )}
          />
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            {PARTNER_ORG_LEAD_CONTACT_CHANNEL_OPTIONS.map((option) => (
              <ChoiceTile
                key={option.value}
                selected={values.contactChannel === option.value}
                label={option.label}
                onClick={() =>
                  setValues((prev) => ({
                    ...prev,
                    contactChannel: option.value,
                  }))
                }
              />
            ))}
          </Stack>
          <TextField
            label="הערות"
            value={values.notes}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, notes: event.target.value }))
            }
            multiline
            minRows={2}
            fullWidth
          />
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          ביטול
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          שמירה
        </Button>
      </DialogActions>
    </Dialog>
  );
}
